import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ShieldCheck, Play, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "../lib/api";
import { trackEvent } from "../lib/tracking";
import SEO from "../components/SEO";
import Divider from "../components/Divider";
import { Reveal } from "../components/Reveal";

export default function CorsoDettaglio() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [openLesson, setOpenLesson] = useState(0);
  const [waitlist, setWaitlist] = useState({ email: "", privacy: false });
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    client.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setCourse(false));
  }, [slug]);

  if (course === null) return <div className="min-h-screen bg-nero" />;
  if (course === false) return (
    <div className="min-h-screen bg-nero flex items-center justify-center">
      <p className="text-panna/60">Corso non trovato. <Link to="/corsi" className="text-oro underline">Torna ai corsi</Link></p>
    </div>
  );

  const isBase = course.slug === "corso-base-il-vino-dalla-vigna-al-calice";

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      trackEvent("begin_checkout", { course: course.slug });
      const { data } = await client.post("/payments/checkout", {
        lookup_key: course.stripe_lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setCheckoutLoading(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlist.privacy) {
      toast.error("Devi accettare la Privacy Policy.");
      return;
    }
    try {
      await client.post("/waitlist", { ...waitlist, course_slug: course.slug });
      trackEvent("waitlist_join", { course: course.slug });
      setWaitlistDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="course-detail-page">
      <SEO title={course.title} path={`/corsi/${course.slug}`} description={course.subtitle} />

      <section className="bg-nero pt-40 pb-24 grain relative">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Link to="/corsi" className="inline-flex items-center gap-2 text-panna/50 hover:text-oro text-sm mb-8 transition-colors">
            <ArrowLeft size={15} /> Tutti i corsi
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-14 items-start">
            <Reveal>
              <span className={`text-[0.65rem] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${
                course.status === "disponibile" ? "bg-oliva/20 text-oliva border border-oliva/40" : "bg-oro/10 text-oro border border-oro/30"
              }`}>
                {course.status === "disponibile" ? "Disponibile" : "In arrivo"}
              </span>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-panna mt-6 leading-tight">{course.title}</h1>
              <p className="mt-5 text-panna/70 leading-relaxed text-base md:text-lg">{course.description}</p>
              <div className="mt-8 rounded-2xl bg-nero-light border border-panna/10 aspect-video flex items-center justify-center">
                <div className="text-center">
                  <Play size={30} className="text-oro mx-auto mb-3" />
                  <p className="text-panna/50 text-sm">Video trailer in arrivo</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-2xl bg-panna p-8 sticky top-28">
                {isBase ? (
                  <>
                    <p className="text-[0.7rem] uppercase tracking-[0.3em] text-bordeaux/70 font-semibold">Offerta di lancio</p>
                    <div className="mt-3 flex items-end gap-3">
                      <span className="font-serif text-2xl text-nero/40 line-through decoration-bordeaux/50 decoration-2">€97</span>
                      <motion.span
                        className="font-serif text-5xl font-bold text-bordeaux leading-none"
                        animate={{ scale: [1, 1.06, 1] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                      >
                        €47
                      </motion.span>
                    </div>
                    <motion.div
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-bordeaux/10 border border-bordeaux/30 px-4 py-1.5"
                      animate={{ opacity: [0.65, 1, 0.65] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <span className="h-2 w-2 rounded-full bg-bordeaux animate-pulse" />
                      <span className="text-xs font-semibold text-bordeaux uppercase tracking-wide">Solo per i primi 100 iscritti</span>
                    </motion.div>
                    <div className="mt-5" data-testid="posti-rimasti">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="font-medium text-nero/70">Posti rimasti al prezzo di lancio</span>
                        <span className="font-serif font-bold text-bordeaux">28<span className="text-nero/40">/100</span></span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-nero/10 overflow-hidden">
                        <motion.div className="h-full rounded-full bg-bordeaux"
                                    initial={{ width: 0 }} animate={{ width: "72%" }}
                                    transition={{ duration: 1.1, ease: "easeOut" }} />
                      </div>
                      <p className="text-[0.7rem] text-nero/45 mt-1.5">72 persone hanno già prenotato il loro posto.</p>
                    </div>
                    <p className="text-sm text-nero/60 mt-4">{course.num_lessons} lezioni · {course.duration} · Livello {course.level}</p>
                    {waitlistDone ? (
                      <p data-testid="reservation-success" className="mt-6 rounded-xl bg-oliva/15 border border-oliva/40 text-oliva px-5 py-4 text-sm">
                        Posto prenotato al prezzo di lancio! Ti scriviamo a breve per confermare i dettagli.
                      </p>
                    ) : (
                      <form onSubmit={joinWaitlist} data-testid="reservation-form" className="mt-6 space-y-4">
                        <input type="email" required placeholder="La tua email" data-testid="reservation-email"
                               value={waitlist.email} onChange={(e) => setWaitlist({ ...waitlist, email: e.target.value })}
                               className="w-full rounded-lg px-4 py-3 bg-panna-dark/60 border border-nero/15 text-nero focus:outline-none focus:ring-2 focus:ring-bordeaux/40" />
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input type="checkbox" data-testid="reservation-privacy" checked={waitlist.privacy}
                                 onChange={(e) => setWaitlist({ ...waitlist, privacy: e.target.checked })} className="mt-1 h-4 w-4 accent-[#7A1626]" />
                          <span className="text-xs text-nero/60">Accetto la <a href="/privacy-policy" className="underline">Privacy Policy</a>.</span>
                        </label>
                        <motion.button type="submit" data-testid="course-buy-button"
                                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                className="w-full rounded-full bg-bordeaux px-8 py-4 font-semibold text-panna border border-oro/30 hover:bg-bordeaux-dark transition-colors">
                          Prenota il tuo corso
                        </motion.button>
                        <p className="text-center text-xs text-nero/50">Nessun pagamento ora: blocchi il prezzo di lancio.</p>
                      </form>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-serif text-4xl font-bold text-bordeaux">€{course.price.toFixed(0)}</p>
                    <p className="text-sm text-nero/60 mt-2">{course.num_lessons} lezioni · {course.duration} · Livello {course.level}</p>
                    {course.status === "disponibile" ? (
                      <button onClick={startCheckout} disabled={checkoutLoading} data-testid="course-buy-button"
                              className="mt-7 w-full rounded-full bg-bordeaux px-8 py-4 font-medium text-panna border border-oro/30 hover:bg-bordeaux-dark transition-colors disabled:opacity-60">
                        {checkoutLoading ? "Reindirizzamento…" : "Acquista il corso"}
                      </button>
                    ) : waitlistDone ? (
                      <p data-testid="waitlist-success" className="mt-7 rounded-xl bg-oliva/15 border border-oliva/40 text-oliva px-5 py-4 text-sm">
                        Sei in lista d'attesa. Ti avviseremo appena il corso apre.
                      </p>
                    ) : (
                      <form onSubmit={joinWaitlist} data-testid="waitlist-form" className="mt-7 space-y-4">
                        <p className="text-sm font-medium text-nero">Iscriviti alla lista d'attesa</p>
                        <input type="email" required placeholder="La tua email" data-testid="waitlist-email"
                               value={waitlist.email} onChange={(e) => setWaitlist({ ...waitlist, email: e.target.value })}
                               className="w-full rounded-lg px-4 py-3 bg-panna-dark/60 border border-nero/15 text-nero focus:outline-none focus:ring-2 focus:ring-bordeaux/40" />
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input type="checkbox" data-testid="waitlist-privacy" checked={waitlist.privacy}
                                 onChange={(e) => setWaitlist({ ...waitlist, privacy: e.target.checked })} className="mt-1 h-4 w-4 accent-[#7A1626]" />
                          <span className="text-xs text-nero/60">Accetto la <a href="/privacy-policy" className="underline">Privacy Policy</a>.</span>
                        </label>
                        <button type="submit" data-testid="waitlist-submit"
                                className="w-full rounded-full border-2 border-bordeaux px-8 py-3.5 font-medium text-bordeaux hover:bg-bordeaux hover:text-panna transition-colors">
                          Avvisami quando esce
                        </button>
                      </form>
                    )}
                  </>
                )}
                <div className="mt-6 flex items-center gap-3 text-xs text-nero/55">
                  <ShieldCheck size={16} className="text-oliva shrink-0" />
                  Garanzia soddisfatti o rimborsati: 14 giorni.
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-panna py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-bordeaux mb-8">Cosa imparerai</h2>
              <ul className="space-y-4">
                {course.learn.map((l) => (
                  <li key={l} className="flex items-start gap-3 text-nero/85">
                    <span className="text-oro mt-1 text-xs">◆</span>
                    <span className="leading-relaxed">{l}</span>
                  </li>
                ))}
              </ul>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-bordeaux mt-14 mb-6">A chi è rivolto</h2>
              <p className="text-nero/80 leading-relaxed">
                Pensato per {course.target.toLowerCase()}: {course.subtitle.toLowerCase()}.
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-bordeaux mt-14 mb-6">Il docente</h2>
              <p className="text-nero/80 leading-relaxed">
                Stefano Cremonese, agente di commercio vino e spirits dal 1996 nel canale Horeca,
                figlio d'arte, formatore con oltre 500 studenti e autore di "Dentro il Calice – Italia".
              </p>
            </Reveal>
          </div>
          <div>
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-bordeaux mb-8">Il programma</h2>
              <div className="space-y-3" data-testid="course-program">
                {course.lessons.map((l, i) => (
                  <div key={i} className="rounded-xl border border-nero/10 bg-panna-dark/40 overflow-hidden">
                    <button data-testid={`lesson-${i}`} onClick={() => setOpenLesson(openLesson === i ? -1 : i)}
                            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
                      <span className="flex items-center gap-4">
                        <span className="font-serif text-oro font-bold">{String(i + 1).padStart(2, "0")}</span>
                        <span className="font-serif font-semibold text-nero">{l.title}</span>
                      </span>
                      <ChevronDown size={17} className={`text-bordeaux transition-transform duration-300 shrink-0 ${openLesson === i ? "rotate-180" : ""}`} />
                    </button>
                    <motion.div initial={false}
                                animate={{ height: openLesson === i ? "auto" : 0, opacity: openLesson === i ? 1 : 0 }}
                                transition={{ duration: 0.3 }} className="overflow-hidden">
                      <p className="px-6 pb-5 pl-[4.4rem] text-sm text-nero/65">{l.topics}</p>
                    </motion.div>
                  </div>
                ))}
              </div>
              <Divider className="mt-14" />
              <p className="text-center text-sm text-nero/55 mt-8">
                Domande sul corso? <Link to="/contatti" className="text-bordeaux underline underline-offset-4">Scrivici</Link>.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
